<script lang="ts" module>
	export interface Props {
		title: string;
		description?: string;
		defaultOpen?: boolean;
	}
</script>

<script lang="ts">
	/**
	 * Imports
	 */
	import { useSlottedElements } from '@src/composables/useSlottedElements';
	import cssVariables from '@src/utils/cssvariables';
	import classNames from 'classnames';
	import { onDestroy, onMount } from 'svelte';
	import { get } from 'svelte/store';
	import ChevronDown from '../icons/chevron-down.svelte';

	/**
	 * Variables
	 */
	let { title = '', description = undefined, defaultOpen = false } = $props();
	let isOpen = $state(defaultOpen);
	let elementHeight: number | undefined = $state(0);
	let currentHeight: number | undefined = $state(0);
	let observer: ResizeObserver | undefined = $state(undefined);
	let childObserver: MutationObserver | undefined = $state(undefined);
	let hasSmoothTransition = $state(false);

	/**
	 * Composables
	 */
	const { element } = useSlottedElements();

	/**
	 * Methods
	 */
	const onToggle = () => {
		isOpen = !isOpen;
	};

	/**
	 * Lifecycle
	 */
	$effect(() => {
		if (isOpen) {
			currentHeight = elementHeight;
		} else {
			currentHeight = 0;
		}
	});

	onMount(() => {
		const currentElement = get(element);
		if (currentElement) {
			const updateHeight = () => {
				const contentHeight = currentElement.scrollHeight;
				elementHeight = contentHeight;
			};

			observer = new ResizeObserver(() => {
				updateHeight();
			});

			// Observe all current and future children
			const observeChildren = (element: Element) => {
				observer?.observe(element);
				element.childNodes.forEach((child) => {
					if (child instanceof Element) {
						observeChildren(child);
					}
				});
			};

			// Watch for new children
			childObserver = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					mutation.addedNodes.forEach((node) => {
						if (node instanceof Element) {
							observeChildren(node);
						}
					});
				});
				updateHeight();
			});

			observer.observe(currentElement);
			childObserver.observe(currentElement, {
				childList: true,
				subtree: true,
			});

			updateHeight();

			setTimeout(() => {
				hasSmoothTransition = true;
			}, 100);
		}
	});

	onDestroy(() => {
		if (observer) {
			observer.disconnect();
			hasSmoothTransition = false;
		}
		if (childObserver) {
			childObserver.disconnect();
		}
	});
</script>

<div
	class={classNames('rf-accordion', { 'rf-accordion--open': isOpen, 'rf-accordion--mounted': hasSmoothTransition })}
	style={cssVariables({ '--rf-accordion-height': `${currentHeight}px` })}
>
	<div
		class="rf-accordion__header"
		onclick={onToggle}
		onkeydown={(e) => e.key === 'Enter' && (isOpen = !isOpen)}
		role="button"
		tabindex="0"
	>
		<div class="rf-accordion__header__content">
			<h3>{title}</h3>
			{#if description}
				<p>{description}</p>
			{/if}
		</div>
		<ChevronDown />
	</div>
	<div bind:this={$element} class="rf-accordion__content">
		<slot />
	</div>
</div>

<style lang="scss" global>
	.rf-accordion {
		display: flex;
		flex-direction: column;
		width: 100%;
		position: relative;
		&__header {
			cursor: pointer;
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			gap: var(--rf-spacing-s, 8px);
			&__content {
				display: flex;
				align-items: baseline;
				gap: var(--rf-spacing-s, 8px);
				width: 100%;
				h3 {
					margin: 0;
				}
				p {
					margin: 0;
					opacity: 0.5;
				}
			}
			svg {
				width: 24px;
				height: 24px;

				.rf-accordion--mounted & {
					transition: transform 0.3s ease-in-out;
				}
			}
		}
		&__content {
			overflow: hidden;
			height: var(--rf-accordion-height, 0);

			& > :first-child {
				padding-top: var(--rf-spacing-s, 8px);
				margin-top: var(--rf-spacing-s, 8px);
				border-top: 1px solid var(--rf-inspector-control-border-color);
			}

			.rf-accordion--mounted & {
				transition: height 0.5s ease;
			}
		}
		&--open {
			.rf-accordion__header {
				svg {
					transform: rotate(180deg);
				}
			}
		}
	}
</style>
